// =====================================================================
// /mes-heures - Vedere grid saptamanala (Faza 3.1 + 3.2 REV-2)
// =====================================================================
// D-002 REV-2 (Pur Odoo): operatorul NU mai apasa "Envoyer".
// Entries raman DRAFT pana cand admin-ul (project approver) le valideaza.
// Schimbari V3 (REV-2):
//   - Eliminat buton "Envoyer" si handler handleSubmitDay
//   - Lock per-cell pe cell.status === 'APPROVED' (nu mai e per-zi pe ts.status)
//   - Badge "✓ Approuvée" verde sub cell APPROVED
//   - Badge "✗ Rejetée" rosu sub cell REJECTED + tooltip cu raison
//   - REJECTED ramane editabil (la upsertEntry backend resetam la DRAFT)
//   - isDayNonJustified simplificat (zero referinte SUBMITTED)
//   - Banner cu indicator rejected count per zi
//
// Backend dependency: timesheets.{getWeek, getJoursNonJustifies,
// getProjectsTasks, upsertEntry, updateEntry, removeEntry}.
// timesheets.submitDay NU mai exista.
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { timesheets as timesheetsApi, users as usersApi } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { useIsMobile } from '../hooks/useIsMobile.jsx';
import TimeInput, { formatMinutes, STANDARD_DAY } from '../components/TimeInput.jsx';
import PeriodLockBanner from '../components/PeriodLockBanner.jsx';
import OvertimeBalanceBanner from '../components/OvertimeBalanceBanner.jsx'; // Faza 3.5 Round 1
import { tachesValables, tacheApresChangementDeDate } from '../lib/validiteTaches.js';

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------

const DOW_LABELS_FR = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const MONTH_LABELS_FR = [
  'janv.', 'févr.', 'mars', 'avril', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];
const STATUS_LABELS_FR = {
  DRAFT: 'Brouillon',
  SUBMITTED: 'Soumis',     // legacy - nu mai apare activ in REV-2
  APPROVED: 'Approuvée',
  REJECTED: 'Rejetée',
};
const STATUS_COLORS = {
  DRAFT:     { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' },
  SUBMITTED: { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd' },
  APPROVED:  { bg: '#dcfce7', fg: '#166534', border: '#86efac' },
  REJECTED:  { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5' },
};
const PRESENCE_LABELS_FR = {
  TRAV: 'Travail', MAL: 'Maladie', AA: 'Abs. aut.', CSSO: 'Sans solde',
  JF: 'Férié', VA: 'Congé', REC: 'Récup.', HC: 'Hors contrat',
  PAR: 'Parental', MAT: 'Matern.', PAT: 'Patern.', PER: 'Personnel', AUTRE: 'Autre',
};

// Non-justified visual style (red header + light pink cell bg)
const NJ_BG = '#fee2e2';
const NJ_BG_LIGHT = '#fef5f5';
const NJ_BORDER = '#dc2626';
const NJ_FG = '#991b1b';

// Per-cell status colors (approved/rejected)
const APPROVED_COLOR = '#16a34a';
const REJECTED_COLOR = '#dc2626';

// ---------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// L-094: check pentru zile viitoare
function isFutureDate(ymd) {
  return ymd > todayYmd();
}
function ymdParts(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return { y, m, d };
}
function addDays(ymd, n) {
  const { y, m, d } = ymdParts(ymd);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}
function weekStartFor(ymd) {
  const { y, m, d } = ymdParts(ymd);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  dt.setUTCDate(dt.getUTCDate() + offset);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}
function formatDateFR(ymd) {
  const { d, m } = ymdParts(ymd);
  return `${d} ${MONTH_LABELS_FR[m - 1]}`;
}
function dowFor(ymd) {
  const { y, m, d } = ymdParts(ymd);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
function extractError(e) {
  return e?.response?.data?.message
    || e?.response?.data?.error
    || e?.message
    || 'Erreur inconnue';
}

/**
 * D-002 REV-2: Verifica daca o zi e "non-justifiee" pentru afisaj rosu.
 * Logica simplificata (eliminat verificarea SUBMITTED):
 *   - Skip weekend (Sa/Du) si JF (nu sunt obligatorii)
 *   - Skip daca presence.type != TRAV
 *   - Marcheaza ca non-just daca total < 7h36
 *
 * Nota: total din getWeek e suma TUTUROR entries (incl. REJECTED).
 * In practica REJECTED e rar (operatorul corecteaza si devine DRAFT),
 * deci aproximare acceptabila pentru visual cue. Backend
 * getJoursNonJustifies face calc precis pe non-REJECTED.
 */
function isDayNonJustified(day) {
  if (!day) return false;
  if (day.isWeekend || day.isHoliday) return false;
  if (!day.presence || day.presence.type !== 'TRAV') return false;
  const total = day.timesheet?.totalMin || 0;
  return total < STANDARD_DAY;
}

// ---------------------------------------------------------------------
// Modal "Ajouter une ligne"
// ---------------------------------------------------------------------

function AjouterLigneModal({ projectsTasks, weekDays, defaultDate, onClose, onSave, delegateMode = false, joursNonJustifies = [] }) {
  const validDefault = weekDays.includes(defaultDate) ? defaultDate : weekDays[0];

  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [date, setDate] = useState(validDefault);
  // Day 11+ (29-05-2026) — sync date state when defaultDate prop changes
  // (e.g. user clicks another date button, or weekData reloads after week-jump).
  // Re-run on weekDays too: if modal opens before async loadAll completes,
  // initial useState used stale weekDays and fell back to weekDays[0].
  useEffect(() => {
    if (defaultDate && weekDays.includes(defaultDate)) {
      setDate(defaultDate);
    }
  }, [defaultDate, weekDays.join('|')]);
  const [durationMin, setDurationMin] = useState(0);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Day 11 — délégation: encoder pour un autre user (mode delegate explicite, bouton séparé)
  const { can } = useAuth();
  const canDelegate = can('timesheets.encode.delegate');
  const [targetUserId, setTargetUserId] = useState('');   // requis en mode delegate
  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => {
    if (canDelegate && delegateMode) {
      usersApi.list({ active: 'true' }).then(d => setAllUsers(Array.isArray(d) ? d : (d.items || d.users || []))).catch(() => {});
    }
  }, [canDelegate, delegateMode]);

  // Catalogue du 01/11/2026 : seulement les tâches valables à la date choisie (octobre = anciennes,
  // novembre et après = nouvelles).
  const tachesDuProjet = useMemo(() => {
    const p = projectsTasks.find((p) => p.id === projectId);
    return p ? p.tasks : [];
  }, [projectsTasks, projectId]);
  const tasks = useMemo(() => tachesValables(tachesDuProjet, date), [tachesDuProjet, date]);

  const selectedTask = useMemo(() => tasks.find((t) => t.id === taskId), [tasks, taskId]);
  const descRequired = selectedTask?.descRequired === true;

  useEffect(() => { setTaskId(''); }, [projectId]);
  // La date change : la tâche choisie passe sur sa remplaçante valable à cette date (ou se vide).
  useEffect(() => {
    setTaskId((id) => tacheApresChangementDeDate(tachesDuProjet, id, date));
  }, [date, tachesDuProjet]);

  async function handleSave() {
    setError(null);
    if (delegateMode && !targetUserId) {
      setError('Choisissez un utilisateur pour qui encoder.');
      return;
    }
    if (!projectId || !taskId || !date) {
      setError('Projet, tâche et date sont obligatoires.');
      return;
    }
    if (durationMin <= 0) {
      setError('Durée requise (> 0 minutes).');
      return;
    }
    if (descRequired && !description.trim()) {
      setError('Description requise pour cette tâche.');
      return;
    }
    // Day 11+ (29-05-2026) — BLOCK encoding for TODAY if any past workday is < 7:36.
    // Operatorul nu poate avansa pe ziua curentă cât timp există zile anterioare neîncheiate.
    // Bypass în mode delegate (admin poate corecta retroactiv pentru alt user).
    if (!delegateMode && date === todayYmd()) {
      const pastIncomplete = (joursNonJustifies || []).filter((it) => it.date < todayYmd());
      if (pastIncomplete.length > 0) {
        const list = pastIncomplete
          .slice(0, 5)
          .map((it) => `${formatDateFR(it.date)} (${formatMinutes(it.totalMin)}/7:36)`)
          .join(', ');
        const more = pastIncomplete.length > 5 ? ` +${pastIncomplete.length - 5} autre(s)` : '';
        setError(
          `Vous devez d'abord compléter les jours passés (< 7:36) avant d'encoder aujourd'hui : ${list}${more}.`
        );
        return;
      }
    }
    setSubmitting(true);
    try {
      await onSave({
        projectId, taskId, date, durationMin,
        description: description.trim() || null,
        ...(delegateMode && targetUserId ? { targetUserId } : {}),
      });
      onClose();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} className="modal-mobile-fullscreen" onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16,
        }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>{delegateMode ? 'Encoder pour un autre utilisateur' : 'Ajouter une ligne'}</h3>
          <button onClick={onClose} style={btnIconStyle} aria-label="Fermer">×</button>
        </div>

        {/* Day 11 — délégation: dropdown visible uniquement en mode delegate (bouton "Encoder pour…") */}
        {delegateMode && (
          <Field label="Encoder pour *">
            <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} style={selectStyle} required>
              <option value="">— Choisir un utilisateur —</option>
              {allUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </Field>
        )}

        <Field label="Projet">
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={selectStyle}>
            <option value="">— Choisir un projet —</option>
            {projectsTasks.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Tâche">
          <select
            value={taskId}
            onChange={(e) => setTaskId(e.target.value)}
            disabled={!projectId}
            style={selectStyle}
          >
            <option value="">— Choisir une tâche —</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {projectId && tasks.length === 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b' }}>
              Aucune tâche de ce projet n&apos;est valable le {formatDateFR(date)}.
            </div>
          )}
        </Field>

        <Field label="Date">
          <select value={date} onChange={(e) => setDate(e.target.value)} style={selectStyle}>
            {weekDays.map((d) => (
              <option key={d} value={d}>
                {DOW_LABELS_FR[dowFor(d)]} {formatDateFR(d)}
              </option>
            ))}
          </select>
          {/* Day 11+ (29-05-2026) — warn proactively when "today" selected and past days incomplete */}
          {!delegateMode && date === todayYmd() && (joursNonJustifies || []).some((it) => it.date < todayYmd()) && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b' }}>
              ⚠ {(joursNonJustifies || []).filter((it) => it.date < todayYmd()).length} jour(s) passé(s) à compléter avant aujourd'hui.
            </div>
          )}
        </Field>

        <Field label="Temps passé">
          {/* liveUpdate: formularul se salveaza cu un buton; fara el, minutele
              tastate ultimele se pierdeau daca nu se iesea din caseta (bug 22-09-2026). */}
          <TimeInput value={durationMin} onChange={setDurationMin} liveUpdate />
          {/* 30-09-2026: maxHours ridicat 11 -> 16, deci eticheta redevine adevarata. */}
          <span style={{ marginLeft: 8, color: '#64748b', fontSize: 12 }}>(max 16h)</span>
        </Field>

        <Field label={`Description${descRequired ? ' *' : ' (facultatif)'}`}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ ...selectStyle, fontFamily: 'inherit', resize: 'vertical' }}
            placeholder={descRequired ? 'Obligatoire pour cette tâche' : 'Facultatif'}
          />
        </Field>

        {error && (
          <div style={{
            background: '#fee2e2', color: '#991b1b', padding: 8,
            borderRadius: 4, fontSize: 13, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={submitting} style={btnSecondaryStyle}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={submitting} style={btnPrimaryStyle}>
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Modal "Modifier ligne" (SLOT I, Day 6)
// Clone of AjouterLigneModal for editing an existing entry.
// ---------------------------------------------------------------------

function EditEntryModal({ projectsTasks, entry, onClose, onSave }) {
  const [projectId, setProjectId] = useState(entry.projectId || '');
  const [taskId, setTaskId] = useState(entry.taskId || '');
  const [durationMin, setDurationMin] = useState(entry.durationMin || 0);
  const [description, setDescription] = useState(entry.description || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Day 13+ — Ligne AUTO issue d'un trajet : projectId présent mais taskId null
  // (le trajet utilise un TripTaskTemplate, pas un Task). Le projet/tâche viennent
  // du trajet et ne sont PAS modifiables ici ; seule la DURÉE est éditable.
  // Sans ce cas, le bouton « Enregistrer » restait désactivé (!taskId) → « rien ne se passe ».
  const isTripAuto = !!entry.projectId && !entry.taskId;

  // Catalogue du 01/11/2026 : tâches valables à la date de la ligne ; la tâche actuelle de la ligne reste
  // toujours proposée (corriger une durée ne force pas à changer de tâche).
  const tasks = useMemo(() => {
    const p = projectsTasks.find((p) => p.id === projectId);
    return p ? tachesValables(p.tasks, entry.date, entry.taskId) : [];
  }, [projectsTasks, projectId, entry.date, entry.taskId]);

  const selectedTask = useMemo(() => tasks.find((t) => t.id === taskId), [tasks, taskId]);
  const descRequired = selectedTask?.descRequired === true;

  useEffect(() => {
    if (isTripAuto) return;   // trajet auto : ne pas réinitialiser la tâche (elle n'existe pas comme Task)
    if (projectId && taskId) {
      const stillValid = tasks.some(t => t.id === taskId);
      if (!stillValid) setTaskId('');
    }
  }, [isTripAuto, projectId, taskId, tasks]);

  async function handleSave() {
    setError(null);
    if (durationMin <= 0) {
      setError('Duree requise (> 0 minutes).');
      return;
    }
    // Trajet auto : projet/tâche viennent du trajet, on ne change QUE la durée + description.
    if (isTripAuto) {
      setSubmitting(true);
      try {
        await onSave({ durationMin, description: description.trim() || null });
        onClose();
      } catch (e) {
        setError(extractError(e));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!projectId || !taskId) {
      setError('Projet et tache sont obligatoires.');
      return;
    }
    if (descRequired && !description.trim()) {
      setError('Description requise pour cette tache.');
      return;
    }
    setSubmitting(true);
    try {
      await onSave({
        projectId,
        taskId,
        durationMin,
        description: description.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} className="modal-mobile-fullscreen" onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16,
        }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Modifier la ligne</h3>
          <button onClick={onClose} style={btnIconStyle} aria-label="Fermer">×</button>
        </div>

        <div style={{ background: '#F1F5F9', padding: 10, borderRadius: 6, marginBottom: 14, fontSize: 13, color: '#475569' }}>
          {entry.date && <>Date : <strong>{formatDateFR(entry.date)}</strong></>}
          {entry.status && entry.status !== 'DRAFT' && (
            <span style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 10, background: '#e2e8f0', fontSize: 11 }}>{entry.status}</span>
          )}
        </div>

        {isTripAuto ? (
          <>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#1e40af' }}>
              Ligne issue d&apos;un trajet — seule la durée est modifiable ici.
            </div>
            <Field label="Projet">
              <div style={{ ...selectStyle, background: '#f8fafc', color: '#475569' }}>{entry.projectName || '—'}</div>
            </Field>
            <Field label="Tache">
              <div style={{ ...selectStyle, background: '#f8fafc', color: '#475569' }}>{entry.taskName || 'Trajet'}</div>
            </Field>
          </>
        ) : (
          <>
            <Field label="Projet">
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={selectStyle}>
                <option value="">— Choisir un projet —</option>
                {projectsTasks.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Tache">
              <select value={taskId} onChange={(e) => setTaskId(e.target.value)} disabled={!projectId} style={selectStyle}>
                <option value="">— Choisir une tache —</option>
                {tasks.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </Field>
          </>
        )}

        <Field label="Duree (h:mm)">
          {/* liveUpdate: idem — modal cu buton de salvare, nu grila auto-save. */}
          <TimeInput value={durationMin} onChange={setDurationMin} ariaLabel="Duree" liveUpdate />
        </Field>

        <Field label={`Description${descRequired ? ' *' : ' (facultatif)'}`}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ ...selectStyle, fontFamily: 'inherit', resize: 'vertical' }}
            placeholder={descRequired ? 'Description obligatoire' : 'Optionnel'}
            maxLength={500}
          />
          <small style={{ color: '#64748b', fontSize: 12 }}>{description.length} / 500</small>
        </Field>

        {error && (
          <div style={{
            background: '#fee2e2', color: '#991b1b', padding: 8,
            borderRadius: 4, fontSize: 13, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={submitting} style={btnSecondaryStyle}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={submitting || durationMin <= 0 || (!isTripAuto && (!projectId || !taskId))} style={btnPrimaryStyle}>
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: 'block', marginBottom: 4, fontSize: 13,
        fontWeight: 500, color: '#475569',
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------
// Banner non-justifiés (REV-2: indicator rejected count)
// ---------------------------------------------------------------------

function NonJustifiesBanner({ items, onAction }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6,
      padding: 12, marginBottom: 16, color: '#991b1b',
    }}>
      <div style={{ marginBottom: 6, fontWeight: 600 }}>
        ⚠ Attention : {items.length} jour{items.length > 1 ? 's' : ''} à compléter
        avant de pouvoir saisir d'autres jours. Cliquez pour ajouter une ligne.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map((it) => {
          const label = `${formatDateFR(it.date)} (${formatMinutes(it.totalMin)} / 7:36)`;
          const rejInfo = it.rejectedCount > 0
            ? ` ⚠ ${it.rejectedCount} rejetée${it.rejectedCount > 1 ? 's' : ''}`
            : '';
          const titleText = it.rejectedReason
            ? `Rejet: ${it.rejectedReason}`
            : `Manque ${formatMinutes(it.missingMin)} - cliquez pour ajouter`;
          return (
            <button
              key={it.date}
              onClick={() => onAction(it.date)}
              style={{
                background: 'white', border: '1px solid #fca5a5', borderRadius: 4,
                padding: '4px 10px', fontSize: 12, color: '#991b1b', cursor: 'pointer',
                fontWeight: 500,
              }}
              title={titleText}
            >
              {label}{rejInfo}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// MobileWeekView — vue verticale jour par jour pour téléphone (Day 11+ 29-05-2026)
// ---------------------------------------------------------------------
function MobileWeekView({ weekData, onDayClick, onEditEntry, onDeleteEntry }) {
  const { days, rows, totals } = weekData;
  // Day 13+ — colapse zile fără entries; expandate doar today/today's-day OR cele expandate manual
  const [expandedDates, setExpandedDates] = useState(() => {
    const set = new Set();
    for (const d of (weekData?.days || [])) {
      if (d.isToday) set.add(d.date);
    }
    return set;
  });
  const toggleExpand = (date) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {days.map((d, di) => {
        const dayTotal = totals.perDay[di] || 0;
        const isWorkDay = !d.isWeekend && !d.isHoliday;
        const nonJust = isDayNonJustified(d);
        const dayLabel = DOW_LABELS_FR[dowFor(d.date)];
        const dateLabel = formatDateFR(d.date);

        // Entries de ce jour (rows × cells[di])
        const dayEntries = [];
        for (const r of rows) {
          const cell = r.cells[di];
          if (cell && (cell.entryId || cell.durationMin > 0)) {
            dayEntries.push({ row: r, cell, dayIdx: di });
          }
        }

        const isExpanded = expandedDates.has(d.date) || dayEntries.length > 0;
        const cardBg = d.isToday ? '#fefce8' : nonJust ? '#fef2f2' : d.isHoliday ? '#fce7f3' : d.isWeekend ? '#f1f5f9' : 'white';
        const borderColor = nonJust ? '#fca5a5' : '#e2e8f0';
        const borderLeft = nonJust ? '3px solid #ef4444' : d.isToday ? '3px solid #facc15' : '3px solid transparent';

        // Compact row pentru zile fără entries (NOT expanded)
        if (!isExpanded && isWorkDay) {
          return (
            <div
              key={d.date}
              onClick={() => toggleExpand(d.date)}
              style={{
                background: cardBg, border: `1px solid ${borderColor}`, borderLeft,
                borderRadius: 8, padding: '10px 12px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: nonJust ? '#991b1b' : '#1e293b' }}>
                {dayLabel} {dateLabel}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: nonJust ? '#dc2626' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMinutes(dayTotal)}
                </div>
                {nonJust && <span style={{ color: '#dc2626', fontSize: 14 }}>⚠</span>}
                <span style={{ color: '#94a3b8', fontSize: 14 }}>›</span>
              </div>
            </div>
          );
        }

        // Compact row weekend / JF
        if (!isWorkDay) {
          return (
            <div
              key={d.date}
              style={{
                background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 8,
                padding: '8px 12px', display: 'flex', justifyContent: 'space-between',
                fontSize: 12, color: '#64748b',
              }}
            >
              <div>{dayLabel} {dateLabel}</div>
              <div style={{ fontWeight: 600 }}>{d.isHoliday ? '🌟 JF' : 'Week-end'}</div>
            </div>
          );
        }

        return (
          <div key={d.date} style={{ background: cardBg, border: `1px solid ${borderColor}`, borderLeft, borderRadius: 10, padding: 12 }}>
            {/* Header zi */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: nonJust ? '#991b1b' : '#1e293b' }}>
                  {dayLabel} {dateLabel}
                  {d.isToday && <span style={{ marginLeft: 6, padding: '1px 6px', background: '#fde047', borderRadius: 4, fontSize: 10 }}>Aujourd'hui</span>}
                  {d.isHoliday && <span style={{ marginLeft: 6, padding: '1px 6px', background: '#ec4899', color: 'white', borderRadius: 4, fontSize: 10 }}>JF</span>}
                  {d.isWeekend && <span style={{ marginLeft: 6, padding: '1px 6px', background: '#94a3b8', color: 'white', borderRadius: 4, fontSize: 10 }}>Week-end</span>}
                </div>
                {d.presence?.type && d.presence.type !== 'TRAV' && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Présence : <strong>{PRESENCE_LABELS_FR[d.presence.type] || d.presence.type}</strong>
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: dayTotal >= STANDARD_DAY ? '#16a34a' : nonJust ? '#dc2626' : '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
                  {formatMinutes(dayTotal)}
                </div>
                {isWorkDay && dayTotal < STANDARD_DAY && (
                  <div style={{ fontSize: 10, color: '#dc2626' }}>
                    manque {formatMinutes(STANDARD_DAY - dayTotal)}
                  </div>
                )}
              </div>
            </div>

            {/* Entries du jour */}
            {dayEntries.length === 0 ? (
              <div style={{ padding: '8px 4px', fontSize: 12, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>
                Aucune entrée
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {dayEntries.map(({ row, cell, dayIdx }) => (
                  <div key={`${row.projectId}-${row.taskId}-${dayIdx}`} style={{
                    background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.projectName}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{row.taskName}</div>
                      {row.description && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.description}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMinutes(cell.durationMin)}
                      </div>
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        {cell.status === 'APPROVED' ? (
                          <span style={{ fontSize: 9, padding: '1px 5px', background: '#dcfce7', color: '#166534', borderRadius: 3 }}>✓</span>
                        ) : cell.status === 'REJECTED' ? (
                          <button onClick={(e) => { e.stopPropagation(); onEditEntry(cell, row); }} style={btnEditSmall}>✏</button>
                        ) : (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); onEditEntry(cell, row); }} style={btnEditSmall}>✏</button>
                            <button onClick={(e) => { e.stopPropagation(); onDeleteEntry(cell.entryId); }} style={btnDelSmall}>🗑</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Buton Ajouter pour ce jour */}
            {isWorkDay && (
              <button onClick={() => onDayClick(d.date)} style={{
                marginTop: 10, width: '100%', padding: 10,
                background: nonJust ? '#dc2626' : '#7c3aed', color: 'white',
                border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}>
                + Ajouter une ligne
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
const btnEditSmall = { padding: '4px 8px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11, cursor: 'pointer' };
const btnDelSmall  = { padding: '4px 8px', background: 'white', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 4, fontSize: 11, cursor: 'pointer' };

// ---------------------------------------------------------------------
// Grid component (REV-2: per-cell lock + badges, fara Envoyer)
// ---------------------------------------------------------------------

function Grid({ weekData, onCellChange, onDeleteEntry, onDayClick, onEditEntry }) {
  const { days, rows, totals } = weekData;
  const cellPad = { padding: '4px 6px', verticalAlign: 'middle', fontSize: 13 };

  return (
    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={{ ...cellPad, textAlign: 'left', minWidth: 180, borderBottom: '2px solid #e2e8f0' }}>
              Projet
            </th>
            <th style={{ ...cellPad, textAlign: 'left', minWidth: 180, borderBottom: '2px solid #e2e8f0' }}>
              Tâche
            </th>
            <th style={{ ...cellPad, textAlign: 'left', minWidth: 220, borderBottom: '2px solid #e2e8f0' }}>
              Description
            </th>
            {days.map((d, di) => {
              const nonJust = isDayNonJustified(d);
              const headerBg = nonJust
                ? NJ_BG
                : (d.isHoliday ? '#fce7f3' : (d.isWeekend ? '#cbd5e1' : '#f8fafc'));
              // Day 11 (29-05-2026) — tooltip "il manque x:xx" sur header
              const dayTotal = totals.perDay[di] || 0;
              const isWorkDay = !d.isWeekend && !d.isHoliday;
              let dayTooltip = '';
              if (isWorkDay) {
                if (dayTotal === 0) {
                  dayTooltip = `Aucune heure encodée — il manque ${formatMinutes(STANDARD_DAY)} pour atteindre 7:36`;
                } else if (dayTotal < STANDARD_DAY) {
                  dayTooltip = `${formatMinutes(dayTotal)} encodé — il manque ${formatMinutes(STANDARD_DAY - dayTotal)} pour atteindre 7:36`;
                } else if (dayTotal === STANDARD_DAY) {
                  dayTooltip = `Journée complète (${formatMinutes(dayTotal)})`;
                } else {
                  dayTooltip = `Heures supplémentaires : +${formatMinutes(dayTotal - STANDARD_DAY)} (total ${formatMinutes(dayTotal)})`;
                }
              }
              if (nonJust) {
                dayTooltip = `Jour non justifié — cliquez pour ajouter une ligne. ${dayTooltip}`;
              }
              return (
                <th
                  key={d.date}
                  onClick={nonJust ? () => onDayClick(d.date) : undefined}
                  title={dayTooltip || undefined}
                  style={{
                    ...cellPad,
                    textAlign: 'center',
                    minWidth: 90,
                    borderBottom: nonJust ? `2px solid ${NJ_BORDER}` : '2px solid #e2e8f0',
                    background: headerBg,
                    cursor: nonJust ? 'pointer' : 'default',
                    color: nonJust ? NJ_FG : 'inherit',
                  }}
                >
                  <div style={{
                    fontWeight: 600, fontSize: 12,
                    color: nonJust ? NJ_FG : '#475569',
                  }}>
                    {DOW_LABELS_FR[d.dow]}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {ymdParts(d.date).d} {MONTH_LABELS_FR[ymdParts(d.date).m - 1]}
                  </div>
                  {d.presence && d.presence.type !== 'TRAV' && (
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                      {PRESENCE_LABELS_FR[d.presence.type] || d.presence.type}
                    </div>
                  )}
                  {d.holidayName && (
                    <div style={{ fontSize: 10, color: '#9f1239', marginTop: 2 }}>
                      {d.holidayName}
                    </div>
                  )}
                  {nonJust && (
                    <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600 }}>
                      ⚠ À compléter
                    </div>
                  )}
                </th>
              );
            })}
            <th style={{ ...cellPad, textAlign: 'right', minWidth: 80, borderBottom: '2px solid #e2e8f0' }}>
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={days.length + 4} style={{
                padding: 32, textAlign: 'center', color: '#94a3b8',
              }}>
                Aucune ligne pour cette semaine. Cliquez « + Ajouter une ligne » pour commencer.
              </td>
            </tr>
          )}
          {rows.map((row, ri) => {
            const rowTotal = row.cells.reduce((s, c) => s + (c?.durationMin || 0), 0);
            return (
              <tr key={ri} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={cellPad} title={row.projectName}>{row.projectName}</td>
                <td style={cellPad} title={row.taskName}>{row.taskName}</td>
                <td style={{ ...cellPad, fontStyle: 'italic', color: '#64748b', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description || ''}>
                  {row.description || '—'}
                </td>
                {row.cells.map((cell, di) => {
                  const day = days[di];
                  const cellStatus = cell?.status || null;
                  // D-002 REV-2: lock per-cell pe APPROVED (NU pe ts.status)
                  const locked = cellStatus === 'APPROVED';
                  const isRejected = cellStatus === 'REJECTED';
                  const nonJust = isDayNonJustified(day);

                  // Tooltip per-cell pe baza statusului
                  let cellWrapperTitle;
                  let cellWrapperBorder = '1px solid transparent';
                  // FAZA B: tooltip clar pe celule goale (nu sunt editabile direct)
                  if (!cell || !cell.entryId) {
                    cellWrapperTitle = 'Pour ajouter une entree, utilisez le bouton "+ Ajouter une ligne"';
                  }
                  if (cellStatus === 'APPROVED') {
                    cellWrapperBorder = `2px solid ${APPROVED_COLOR}`;
                    cellWrapperTitle = cell.approvedAt
                      ? `Approuvée le ${new Date(cell.approvedAt).toLocaleDateString('fr-BE')}`
                      : 'Approuvée';
                  } else if (cellStatus === 'REJECTED') {
                    cellWrapperBorder = `2px solid ${REJECTED_COLOR}`;
                    cellWrapperTitle = cell.rejectedReason
                      ? `Rejetée: ${cell.rejectedReason}`
                      : 'Rejetée - modifiez pour soumettre à nouveau';
                  }

                  return (
                    <td key={di} style={{
                      ...cellPad, textAlign: 'center',
                      background: nonJust ? NJ_BG_LIGHT : (day.isWeekend ? '#e2e8f0' : (isFutureDate(day.date) ? '#f1f5f9' : 'white')),
                    }}>
                      <div
                        title={cellWrapperTitle}
                        style={{
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'center', gap: 2,
                          border: cellWrapperBorder,
                          borderRadius: 4, padding: '1px',
                        }}
                      >
                        <TimeInput
                          value={cell?.durationMin || 0}
                          disabled={locked || !cell?.entryId || isFutureDate(day.date)}
                          compact={true}
                          onChange={(newMin) => {
                            if (newMin === (cell?.durationMin || 0)) return;
                            onCellChange(row, di, newMin);
                          }}
                          ariaLabel={`${row.projectName} ${row.taskName} ${day.date}`}
                        />
                        {cell?.entryId && !isFutureDate(day.date) && onEditEntry && (
                          <button
                            onClick={() => onEditEntry(cell, row)}
                            title={locked ? 'Corriger (entrée approuvée — autorisé pour entries auto-trajet)' : 'Modifier'}
                            style={{
                              background: 'transparent', border: 'none', cursor: 'pointer',
                              color: locked ? '#f59e0b' : '#94a3b8', fontSize: 13, lineHeight: 1, padding: '2px 4px',
                            }}
                          >✏️</button>
                        )}
                        {cell?.entryId && !locked && (
                          <button
                            onClick={() => onDeleteEntry(cell.entryId)}
                            title="Supprimer"
                            style={{
                              background: 'transparent', border: 'none', cursor: 'pointer',
                              color: '#94a3b8', fontSize: 14, lineHeight: 1, padding: '2px 4px',
                            }}
                          >×</button>
                        )}
                      </div>
                      {/* Status indicator sub cell */}
                      {cellStatus === 'APPROVED' && (
                        <div style={{
                          fontSize: 9, color: APPROVED_COLOR,
                          fontWeight: 600, marginTop: 2,
                        }}>
                          ✓ Approuvée
                        </div>
                      )}
                      {isRejected && (
                        <div style={{
                          fontSize: 9, color: REJECTED_COLOR,
                          fontWeight: 600, marginTop: 2,
                        }}>
                          ✗ Rejetée
                        </div>
                      )}
                    </td>
                  );
                })}
                <td style={{
                  ...cellPad, textAlign: 'right', fontWeight: 600,
                  fontFamily: 'ui-monospace, monospace',
                }}>
                  {formatMinutes(rowTotal)}
                </td>
              </tr>
            );
          })}

          {/* Footer totaluri (D-002 REV-2: zero butoane Envoyer) */}
          <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
            <td colSpan={3} style={{ ...cellPad, fontWeight: 700 }}>Total</td>
            {totals.perDay.map((t, di) => {
              const day = days[di];
              const isWorkingDay = !day.isWeekend && !day.isHoliday;
              const isUnder = isWorkingDay && t > 0 && t < STANDARD_DAY;
              const nonJust = isDayNonJustified(day);
              // Day 11 (29-05-2026) — tooltip "il manque x:xx"
              let dayTooltip = '';
              if (isWorkingDay) {
                if (t === 0) {
                  dayTooltip = `Aucune heure encodée — il manque ${formatMinutes(STANDARD_DAY)} pour atteindre 7:36`;
                } else if (t < STANDARD_DAY) {
                  dayTooltip = `${formatMinutes(t)} encodé — il manque ${formatMinutes(STANDARD_DAY - t)} pour atteindre 7:36`;
                } else if (t === STANDARD_DAY) {
                  dayTooltip = `Journée complète (${formatMinutes(t)})`;
                } else {
                  dayTooltip = `Heures supplémentaires : +${formatMinutes(t - STANDARD_DAY)} (total ${formatMinutes(t)})`;
                }
              }
              if (nonJust) dayTooltip = `Cliquez pour ajouter une ligne. ${dayTooltip}`;
              return (
                <td
                  key={di}
                  onClick={nonJust ? () => onDayClick(day.date) : undefined}
                  title={dayTooltip || undefined}
                  style={{
                    ...cellPad, textAlign: 'center',
                    background: nonJust ? NJ_BG : (day.isWeekend ? '#cbd5e1' : 'transparent'),
                    cursor: nonJust ? 'pointer' : 'default',
                  }}
                >
                  <div style={{
                    fontWeight: 700, fontFamily: 'ui-monospace, monospace',
                    color: isUnder || nonJust ? NJ_FG : (t >= STANDARD_DAY ? '#166534' : '#475569'),
                  }}>
                    {formatMinutes(t)}
                  </div>
                </td>
              );
            })}
            <td style={{
              ...cellPad, textAlign: 'right', fontWeight: 700,
              fontFamily: 'ui-monospace, monospace', fontSize: 14,
            }}>
              {formatMinutes(totals.grand)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function statusBadgeStyle(status) {
  const c = STATUS_COLORS[status] || { bg: '#f3f4f6', fg: '#374151', border: '#e5e7eb' };
  return {
    display: 'inline-block', marginTop: 4,
    background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
    fontSize: 10, padding: '1px 6px', borderRadius: 3, fontWeight: 600,
  };
}

// ---------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------

export default function MesHeures({ embedded = false }) {
  const { user, loading: authLoading, can } = useAuth();
  const canDelegate = can('timesheets.encode.delegate');
  const isMobile = useIsMobile(640);
  const [weekStart, setWeekStart] = useState(weekStartFor(todayYmd()));
  const [weekData, setWeekData] = useState(null);
  const [nonJustifies, setNonJustifies] = useState([]);
  const [projectsTasks, setProjectsTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [delegateMode, setDelegateMode] = useState(false);   // Day 11 — mode "Encoder pour…"
  const [modalDate, setModalDate] = useState(null);
  const [editEntryTarget, setEditEntryTarget] = useState(null);  // SLOT I (Day 6)

  // Faza 3.4 D-007: lunile (YYYY-MM) acoperite de saptamana curenta.
  // Folosit pentru filter PeriodLockBanner: nu aratam lock-uri pe alte luni
  // care nu apar in vedere curenta. O saptamana poate cuprinde 1 sau 2 luni
  // (cazul tranzitie cum ar fi 27 apr - 3 mai).
  const relevantPeriods = useMemo(() => {
    if (!weekData?.days) return [];
    const set = new Set();
    for (const d of weekData.days) {
      set.add(d.date.slice(0, 7)); // "YYYY-MM-DD" -> "YYYY-MM"
    }
    return Array.from(set);
  }, [weekData]);

  async function loadAll(currentWeekStart) {
    setLoading(true);
    setError(null);
    try {
      const [week, nj, pt] = await Promise.all([
        timesheetsApi.getWeek(currentWeekStart),
        timesheetsApi.getJoursNonJustifies(),
        timesheetsApi.getProjectsTasks(),
      ]);
      setWeekData(week);
      setNonJustifies(nj);
      setProjectsTasks(pt);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!authLoading) loadAll(weekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, authLoading]);

  const reloadWeek = () => loadAll(weekStart);

  async function handleCellChange(row, dayIdx, newMin) {
    const cell = row.cells[dayIdx];
    // FAZA B: celula goala = nu se editeaza aici.
    // Pentru entry nou se foloseste butonul "+ Ajouter une ligne".
    // Asta evita ambiguitatea "in ce zi creez entry-ul" si forteaza modal-ul cu validari.
    if (!cell || !cell.entryId) {
      return;
    }
    try {
      await timesheetsApi.updateEntry(cell.entryId, { durationMin: newMin });
      await reloadWeek();
    } catch (e) {
      setError(extractError(e));
    }
  }

  async function handleAddRow(payload) {
    await timesheetsApi.createEntry(payload);
    await reloadWeek();
  }

  async function handleDeleteEntry(entryId) {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await timesheetsApi.removeEntry(entryId);
      await reloadWeek();
    } catch (e) {
      setError(extractError(e));
    }
  }

  /**
   * V2: Deschide modal Ajouter une ligne cu data preselectata.
   * Daca data e in alta saptamana, jump la acea saptamana intai.
   */
  function openModalForDate(date) {
    const newWs = weekStartFor(date);
    if (newWs !== weekStart) {
      setWeekStart(newWs);
    }
    setModalDate(date);
    setShowAddRow(true);
  }

  // À chaque ouverture : liste des tâches relue (le serveur ne montre les nouvelles tâches qu'à partir de leur
  // premier jour ; une page restée ouverte la veille les reçoit ainsi sans recharger).
  useEffect(() => {
    if (!showAddRow) return;
    timesheetsApi.getProjectsTasks().then(setProjectsTasks).catch(() => {});
  }, [showAddRow]);

  function closeModal() {
    setShowAddRow(false);
    setModalDate(null);
    setDelegateMode(false);
  }

  // SLOT I (Day 6) — open Edit modal for an entry
  function openEditEntry(cell, row) {
    if (!cell || !cell.entryId) return;
    setEditEntryTarget({
      entryId: cell.entryId,
      projectId: row.projectId,
      taskId: row.taskId,
      projectName: row.projectName || '',
      taskName: row.taskName || '',
      description: row.description || '',
      durationMin: cell.durationMin || 0,
      status: cell.status || 'DRAFT',
      date: cell.date || null,
    });
  }

  async function handleEditSave(payload) {
    await timesheetsApi.updateEntry(editEntryTarget.entryId, payload);
    await reloadWeek();
  }

  function goPrev()  { setWeekStart(addDays(weekStart, -7)); }
  function goNext()  { setWeekStart(addDays(weekStart, +7)); }
  function goToday() { setWeekStart(weekStartFor(todayYmd())); }

  if (authLoading) return <div style={{ padding: 24 }}>Chargement…</div>;
  if (!user) return <div style={{ padding: 24 }}>Authentification requise.</div>;

  const defaultDateForModal = weekData
    ? (weekData.days.find((d) => d.date === todayYmd()) || weekData.days[0]).date
    : todayYmd();

  return (
    <div style={{ padding: embedded ? 0 : 24, maxWidth: 1400, margin: '0 auto' }}>
      {!embedded && <h1 style={{ margin: '0 0 16px 0', fontSize: 24 }}>Mes feuilles de temps</h1>}

      {error && (
        <div style={{
          background: '#fee2e2', color: '#991b1b', padding: 12,
          borderRadius: 6, marginBottom: 16, display: 'flex', alignItems: 'center',
        }}>
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ ...btnIconStyle, color: '#991b1b', borderColor: '#fca5a5' }}
          >×</button>
        </div>
      )}

      <PeriodLockBanner relevantPeriods={relevantPeriods} />

      {/* Faza 3.4: skip ziua curenta din banner-ul rosu non-justifies.
          Operatorul abia incepe ziua, nu trebuie marcat ca "a completer".
          Banner rosu apare din ziua urmatoare daca ramane sub 7h36. */}
      <NonJustifiesBanner
        items={nonJustifies.filter((it) => it.date !== todayYmd())}
        onAction={openModalForDate}
      />

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 12, flexWrap: 'wrap',
      }}>
        <button onClick={goToday} style={btnSecondaryStyle}>Aujourd'hui</button>
        <button onClick={goPrev} style={btnIconStyle} title="Semaine précédente">◀</button>
        <span style={{ fontWeight: 500, minWidth: 240 }}>
          Semaine du {formatDateFR(weekStart)} au {formatDateFR(addDays(weekStart, 6))}
        </span>
        <button onClick={goNext} style={btnIconStyle} title="Semaine suivante">▶</button>
          <OvertimeBalanceBanner />
        <div style={{ flex: 1 }} />
        {/* Day 11+ — pe mobil butoanele sunt înlocuite de "+Ajouter" per-zi în MobileWeekView */}
        <button className="hide-mobile" onClick={() => { setModalDate(null); setDelegateMode(false); setShowAddRow(true); }} style={btnPrimaryStyle}>
          + Ajouter une ligne
        </button>
        {canDelegate && (
          <button className="hide-mobile" onClick={() => { setModalDate(null); setDelegateMode(true); setShowAddRow(true); }} style={btnSecondaryStyle} title="Encoder une ligne au nom d'un autre utilisateur">
            + Encoder pour…
          </button>
        )}
      </div>

      {loading && !weekData ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Chargement…</div>
      ) : weekData ? (
        isMobile ? (
          <MobileWeekView
            weekData={weekData}
            onDayClick={openModalForDate}
            onEditEntry={openEditEntry}
            onDeleteEntry={handleDeleteEntry}
          />
        ) : (
        <Grid
          weekData={weekData}
          onCellChange={handleCellChange}
          onEditEntry={openEditEntry}
          onDeleteEntry={handleDeleteEntry}
          onDayClick={openModalForDate}
        />)
      ) : null}

      {showAddRow && weekData && (
        <AjouterLigneModal
          projectsTasks={projectsTasks}
          weekDays={weekData.days.map((d) => d.date)}
          defaultDate={modalDate || defaultDateForModal}
          onClose={closeModal}
          onSave={handleAddRow}
          delegateMode={delegateMode}
          joursNonJustifies={nonJustifies}
        />
      )}

      {editEntryTarget && (
        <EditEntryModal
          projectsTasks={projectsTasks}
          entry={editEntryTarget}
          onClose={() => setEditEntryTarget(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Common styles
// ---------------------------------------------------------------------

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};

const modalStyle = {
  background: 'white', borderRadius: 8, padding: 24,
  width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
};

const selectStyle = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid #cbd5e1', borderRadius: 4, background: 'white',
  boxSizing: 'border-box',
};

const btnPrimaryStyle = {
  background: '#1e40af', color: 'white', border: 'none',
  padding: '8px 16px', borderRadius: 4, cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};

const btnSecondaryStyle = {
  background: 'white', color: '#475569', border: '1px solid #cbd5e1',
  padding: '6px 12px', borderRadius: 4, cursor: 'pointer',
  fontSize: 13, fontWeight: 500,
};

const btnIconStyle = {
  background: 'transparent', border: '1px solid #cbd5e1',
  width: 32, height: 32, borderRadius: 4, cursor: 'pointer',
  fontSize: 14, color: '#475569', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
};